import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { Button, Typography } from '@packages/ui/shared';
import { useAuthState } from '@packages/contexts/auth';
import { getLogger } from '@packages/core/logging';
import { docsService } from '@web/services/singletons';
import { usePublicationsDispatch, usePublicationsState } from '../stis-publications/context/publications.hooks';
import { publicationsActions } from '../stis-publications/context/publications.state';

const logger = getLogger('vin-sessions');

type HelpBubbleProps = {
    onClose: () => void;
    feedbackContext: {
        query: string;
        publicationTypeIds: string[];
        modelCodeId: string;
        modelYear: string;
        carlineId: string;
        searchType: string;
        docStatus?: string;
    };
};

const useStyles = createUseStyles({
    '@keyframes fadeInUp': {
        from: {
            opacity: 0,
            transform: 'translateY(40px)',
        },
        to: {
            opacity: 1,
            transform: 'translateY(0)',
        },
    },
    '@keyframes fadeOutDown': {
        from: {
            opacity: 1,
            transform: 'translateY(0)',
        },
        to: {
            opacity: 0,
            transform: 'translateY(40px)',
        },
    },
    textArea: {
        width: '100%',
        resize: 'none',
        fontSize: '14px',
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: 'none',
    },
    bubbleContainer: {
        position: 'absolute',
        width: '90%',
        maxWidth: '528px',
        height: '90%',
        maxHeight: '360px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        opacity: 1,
        animation: '$fadeInUp 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
        transition: 'box-shadow 0.3s cubic-bezier(0.23, 1, 0.32, 1), background 0.3s, opacity 0.4s, transform 0.4s',
        '&.fadeOut': {
            animation: '$fadeOutDown 0.4s cubic-bezier(0.23, 1, 0.32, 1) forwards',
        },
    },
    header: {
        backgroundColor: '#F8F8F8',
        width: '100%',
        height: '64px',
        padding: '24px',
        display: 'flex',
        alignItems: 'center',
    },
    content: {
        padding: '12px',
        flex: 1,
    },
    footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        padding: '8px 12px',
        borderTop: '1px solid #E0E0E0',
    },
    checkboxRow: {
        display: 'flex',
        alignItems: 'center',
    },
    buttonRow: {
        display: 'flex',
        gap: '8px',
    },
});

export const HelpBubble: React.FC<HelpBubbleProps> = ({ onClose, feedbackContext }) => {
    const [feedback, setFeedback] = useState<string>('');
    const [dontAskAgain, setDontAskAgain] = useState<boolean>(false);
    const classes = useStyles();
    const { account } = useAuthState();
    const publicationDispatch = usePublicationsDispatch();
    const { stisFeedbackPrompt } = usePublicationsState();
    const [selectedRating, setSelectedRating] = useState<number>(0);
    const [hoverRating, setHoverRating] = useState<number>(0);

    useEffect(() => {
        setDontAskAgain(stisFeedbackPrompt);
    }, [stisFeedbackPrompt]);

    const handlePreferenceChange = useCallback(async () => {
        if (!account?.accountId) {
            return;
        }
        try {
            const response = await docsService.changePreference({
                accountId: account.accountId,
                body: { stisFeedbackPrompt: dontAskAgain },
            });
            if (response.success) {
                publicationDispatch(
                    publicationsActions.setStisFeedbackPrompt({
                        stisFeedbackPrompt: response.data.account.stisFeedbackPrompt,
                    })
                );
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: 'Preferences updated successfully',
                            iconColor: 'white',
                            iconName: 'checkmark',
                        },
                    })
                );
            } else {
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: 'Failed to update preferences',
                            iconColor: 'white',
                            iconName: 'x-close',
                        },
                    })
                );
            }
        } catch (error) {
            publicationDispatch(
                publicationsActions.setToastMessage({
                    toast: {
                        toastMessage: 'An error occurred while updating preferences',
                        iconColor: 'white',
                        iconName: 'x-close',
                    },
                })
            );
            logger.error('Failed to update the feedback preferences', { error });
        }
        onClose();
    }, [dontAskAgain, account, publicationDispatch, onClose, stisFeedbackPrompt]);

    const submitFeedback = useCallback(async () => {
        const body = {
            feedbackContext: feedbackContext,
            comments: feedback,
            rating: selectedRating,
        };

        try {
            const response = await docsService.submitFeedback({ body });
            publicationDispatch(publicationsActions.setStisFeedbackPrompt({ stisFeedbackPrompt: dontAskAgain }));
            if (response.success) {
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: 'Thank you! Your feedback has been submitted',
                            iconColor: 'white',
                            iconName: 'checkmark',
                        },
                    })
                );
                onClose();
            } else {
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: 'Failed to save feedback',
                            iconColor: 'white',
                            iconName: 'x-close',
                        },
                    })
                );
                logger.error('Failed to submit the feedback', { error: response.data });
            }
        } catch (error) {
            publicationDispatch(
                publicationsActions.setToastMessage({
                    toast: {
                        toastMessage: 'An error occurred while submitting feedback',
                        iconColor: 'white',
                        iconName: 'x-close',
                    },
                })
            );
            logger.error('Failed to submit the feedback', { error });
        }
    }, [selectedRating, dontAskAgain, feedback, feedbackContext, publicationDispatch, onClose]);

    return (
        <div className={classes.bubbleContainer} style={{ bottom: 84, right: 24 }}>
            <div className={classes.header}>
                <Typography
                    style={{
                        fontSize: 21,
                        fontWeight: 600,
                        color: '#71424',
                    }}
                >
                    Did you find what you were looking for?
                </Typography>
            </div>
            <div className={classes.content}>
                <div style={{ marginBottom: 12 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                        <span
                            key={star}
                            style={{
                                cursor: 'pointer',
                                fontSize: 28,
                                color: star <= (hoverRating || selectedRating) ? '#436ca1' : '#999da3',
                                marginRight: 4,
                                transition: 'color 0.2s',
                            }}
                            onClick={() => (selectedRating === star ? setSelectedRating(0) : setSelectedRating(star))}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            data-testid={`star-${star}`}
                        >
                            {star <= (hoverRating || selectedRating) ? '★' : '☆'}
                        </span>
                    ))}
                </div>
                <Form.Control
                    as="textarea"
                    rows={3}
                    className={classes.textArea}
                    placeholder="Write your feedback here..."
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    style={{ border: '1px solid #999da3' }}
                />
            </div>
            <div className={classes.footer}>
                <Form.Check
                    type="checkbox"
                    label="Don't ask me again."
                    id="dontAskAgainCheckbox"
                    checked={dontAskAgain}
                    onChange={(e) => setDontAskAgain(e.target.checked)}
                />
                <div className={classes.buttonRow}>
                    <Button variant="ghost-blue" onPress={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onPress={
                            feedback.trim() || selectedRating
                                ? submitFeedback
                                : dontAskAgain !== stisFeedbackPrompt
                                  ? handlePreferenceChange
                                  : undefined
                        }
                        disabled={feedback.trim() || selectedRating ? false : dontAskAgain === stisFeedbackPrompt}
                    >
                        {feedback.trim() || selectedRating
                            ? 'Send'
                            : dontAskAgain !== stisFeedbackPrompt
                              ? 'Save preference'
                              : 'Send'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
